import React, { useState, useEffect, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import InputMask from "react-input-mask";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import injectCollectJS from "../injectCollect";
import { useLanguage } from "../contexts/LanguageContext";

const PACKS = [
  { key: "single", spins: 1, price: 5,  icon: "🎯" },
  { key: "pack",   spins: 5, price: 20, icon: "🎁", hasBadge: true },
];

const BuySpinsModal = ({ show, onHide, profile, onSuccess, defaultPack = "pack" }) => {
  const L = useLanguage();
  const [selectedPack, setSelectedPack] = useState(defaultPack);
  useEffect(() => { if (show) setSelectedPack(defaultPack); }, [show, defaultPack]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const cardRef = useRef(null);
  const expRef = useRef(null);
  const cvvRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Default-select the most-recent card if user has any
  useEffect(() => {
    if (!show) return;
    const ids = Object.keys(profile?.cards || {});
    if (ids.length > 0 && !selectedCard) setSelectedCard(ids[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const pack = PACKS.find(p => p.key === selectedPack);

  const purchaseWithCard = async (e) => {
    if (e) e.preventDefault();
    if (!selectedCard) { setError(L.buySpins.errSelectPayment); return; }
    setLoading(true); setError("");
    try {
      const body = { pack: selectedPack };
      if (selectedCard === "addCard") {
        const card = cardRef.current?.value.replace(/\s/g, "");
        const exp = expRef.current?.value;
        const cvv = cvvRef.current?.value;
        if (!card || !exp || !cvv) { setError(L.buySpins.errFillCardDetails); setLoading(false); return; }
        body.card = card; body.exp = exp; body.cvv = cvv;
      } else {
        body.cardid = selectedCard;
      }
      const res = await axiosClient.post(`${API_BASE_URL}/dailybonus/buy-spins`, body);
      if (res.data.success) {
        onSuccess?.(res.data);
        onHide();
      } else {
        setError(res.data.error || L.buySpins.errPurchaseFailed);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || L.buySpins.errPurchaseFailed);
    } finally {
      setLoading(false);
    }
  };

  const purchaseApplePay = async (paymenttoken) => {
    setLoading(true); setError("");
    try {
      const res = await axiosClient.post(`${API_BASE_URL}/dailybonus/buy-spins`, {
        pack: selectedPack, paymenttoken,
      });
      if (res.data.success) {
        onSuccess?.(res.data);
        onHide();
      } else {
        setError(res.data.error || L.buySpins.errPurchaseFailed);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || L.buySpins.errPurchaseFailed);
    } finally {
      setLoading(false);
    }
  };

  // Init Apple Pay when modal opens / pack changes
  useEffect(() => {
    if (!show) return;
    const price = pack?.price || 0;
    const t = setTimeout(() => {
      if (!mountedRef.current) return;
      try {
        injectCollectJS(
          "https://secure.nmi.com/token/Collect.js",
          "9t4q59-U47k7W-Cj7Ty9-MbC6Y6",
          purchaseApplePay,
          price.toString()
        ).catch(() => {});
      } catch { /* noop */ }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedPack]);

  const cards = profile?.cards || {};
  const cardIds = Object.keys(cards);

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <div style={{ padding: "1.5rem 1.5rem 0" }}>
        <div className="flex items-center justify-between">
          <div>
            <h5 style={{ fontWeight: 700, color: "#0a2540", fontSize: "1.15rem", marginBottom: "2px" }}>{L.buySpins.title}</h5>
            <p style={{ color: "#68778d", fontSize: "0.82rem", margin: 0 }}>{L.buySpins.subtitle}</p>
          </div>
          <button onClick={onHide} style={{
            background: "none", border: "none", cursor: "pointer", padding: "4px",
            color: "#68778d", fontSize: "1.2rem", lineHeight: 1,
          }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>

      <Modal.Body style={{ padding: "1.25rem 1.5rem 1.5rem" }}>
        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
            padding: "10px 14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px",
          }}>
            <i className="fas fa-exclamation-circle" style={{ color: "#dc2626", fontSize: "0.85rem" }} />
            <span style={{ color: "#991b1b", fontSize: "0.82rem", fontWeight: 500 }}>{error}</span>
          </div>
        )}

        {/* Pack selection */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#68778d", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px", display: "block" }}>
            {L.buySpins.selectPack}
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {PACKS.map(p => {
              const selected = selectedPack === p.key;
              const txt = L.buySpins.packs[p.key];
              return (
                <div key={p.key} onClick={() => setSelectedPack(p.key)} style={{
                  position: "relative",
                  padding: "14px 12px", borderRadius: "10px", cursor: "pointer", textAlign: "center",
                  border: selected ? "2px solid #635bff" : "1.5px solid #e3e8ee",
                  background: selected ? "#f5f3ff" : "#fff",
                  boxShadow: selected ? "0 0 0 3px rgba(99,91,255,0.1)" : "none",
                  transition: "all 0.15s",
                }}>
                  {p.hasBadge && (
                    <span style={{
                      position: "absolute", top: -8, right: -6,
                      padding: "3px 8px",
                      borderRadius: 999, fontSize: "0.62rem", fontWeight: 800,
                      background: "linear-gradient(135deg, #16a34a, #15803d)", color: "#fff",
                      letterSpacing: "0.02em",
                      boxShadow: "0 4px 10px -3px rgba(22,163,74,0.45)",
                      whiteSpace: "nowrap",
                    }}>{txt.badge}</span>
                  )}
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{p.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0a2540" }}>{txt.label}</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: selected ? "#635bff" : "#0a2540", marginTop: 2 }}>
                    ${p.price}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{
          background: "#f6f9fc", borderRadius: "10px", padding: "14px 16px", marginBottom: 20,
          border: "1px solid #e3e8ee",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.82rem", color: "#68778d", fontWeight: 500 }}>
              {L.buySpins.spinsCount(pack?.spins)}
            </span>
            <span style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0a2540" }}>${pack?.price}</span>
          </div>
        </div>

        {/* Payment method */}
        <Form onSubmit={purchaseWithCard}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#68778d", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px", display: "block" }}>
              {L.buySpins.paymentMethod}
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {cardIds.map(id => {
                const card = cards[id];
                const cn = card.cardnumber || "";
                return (
                  <div key={id} onClick={() => { setSelectedCard(id); setError(""); }} style={{
                    padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                    border: selectedCard === id ? "2px solid #635bff" : "1.5px solid #e3e8ee",
                    background: selectedCard === id ? "#f5f3ff" : "#fff",
                    transition: "all 0.15s", display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      background: selectedCard === id ? "#ede9fe" : "#f1f5f9",
                    }}>
                      {cn.startsWith("4") && <i className="fa-brands fa-cc-visa" style={{ color: "#635bff" }} />}
                      {cn.startsWith("5") && <i className="fa-brands fa-cc-mastercard" style={{ color: "#635bff" }} />}
                      {cn.startsWith("3") && <i className="fa-brands fa-cc-amex" style={{ color: "#635bff" }} />}
                      {!"345".includes(cn[0] || "") && <i className="fa-solid fa-credit-card" style={{ color: "#635bff" }} />}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#0a2540" }}>•••• {cn.slice(-4)}</span>
                    <span style={{ fontSize: "0.78rem", color: "#68778d" }}>{card.exp}</span>
                    {selectedCard === id && <i className="fas fa-check-circle" style={{ marginLeft: "auto", color: "#635bff" }} />}
                  </div>
                );
              })}
              <div onClick={() => { setSelectedCard("addCard"); setError(""); }} style={{
                padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                border: selectedCard === "addCard" ? "2px solid #635bff" : "1.5px dashed #d1d5db",
                background: selectedCard === "addCard" ? "#f5f3ff" : "#fff",
                transition: "all 0.15s", display: "flex", alignItems: "center", gap: 10,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  background: selectedCard === "addCard" ? "#ede9fe" : "#f1f5f9",
                }}>
                  <i className="fas fa-plus" style={{ color: "#635bff", fontSize: "0.75rem" }} />
                </div>
                <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#0a2540" }}>{L.buySpins.addNewCard}</span>
                {selectedCard === "addCard" && <i className="fas fa-check-circle" style={{ marginLeft: "auto", color: "#635bff" }} />}
              </div>
            </div>
          </div>

          {(cardIds.length === 0 || selectedCard === "addCard") && (
            <div style={{
              background: "#f6f9fc", borderRadius: 10, padding: 16, marginBottom: 16,
              border: "1px solid #e3e8ee",
            }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#68778d", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "block" }}>{L.buySpins.cardNumber}</label>
                <Form.Control as={InputMask} type="text" ref={cardRef} placeholder="1234 5678 9012 3456" mask="9999 9999 9999 9999" maskChar="" required style={{ borderRadius: 8, fontSize: "0.88rem" }} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#68778d", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "block" }}>{L.buySpins.expiry}</label>
                  <Form.Control as={InputMask} maskChar="" type="text" ref={expRef} placeholder="MM/YY" mask="99/99" required style={{ borderRadius: 8, fontSize: "0.88rem" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#68778d", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "block" }}>{L.buySpins.cvv}</label>
                  <Form.Control as={InputMask} maskChar="" type="text" ref={cvvRef} placeholder="123" mask="9999" required style={{ borderRadius: 8, fontSize: "0.88rem" }} />
                </div>
              </div>
            </div>
          )}

          <button type="submit" disabled={loading || !selectedCard} style={{
            width: "100%", padding: 12, borderRadius: 10, border: "none",
            fontWeight: 700, fontSize: "0.9rem",
            cursor: loading || !selectedCard ? "not-allowed" : "pointer",
            color: "#fff",
            background: loading || !selectedCard ? "#c4b5fd" : "linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)",
            boxShadow: loading || !selectedCard ? "none" : "0 2px 8px rgba(99,91,255,0.3)",
            opacity: loading || !selectedCard ? 0.7 : 1,
          }}>
            {loading ? (<><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} /> {L.buySpins.processing}</>) : <>{L.buySpins.pay(pack?.price)}</>}
          </button>

          {window.location.hostname !== "localhost" && <div style={{ marginTop: 12 }} id="applepaybutton" />}

          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16, paddingTop: 12, borderTop: "1px solid #e3e8ee" }}>
            <span style={{ fontSize: "0.72rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}><i className="fas fa-lock" /> {L.buySpins.secure}</span>
            <span style={{ fontSize: "0.72rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}><i className="fas fa-bolt" /> {L.buySpins.instant}</span>
            <span style={{ fontSize: "0.72rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}><i className="fas fa-shield-halved" /> {L.buySpins.encrypted}</span>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default BuySpinsModal;
